import { useState, useEffect, useCallback } from 'react';
import { IMapController } from '../../../lib/map/types';

interface UseMapToolbarOptions {
  mapController: IMapController | null;
}

export const useMapToolbar = ({ mapController }: UseMapToolbarOptions) => {
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [hasSelectedMarker, setHasSelectedMarker] = useState(false);

  const updateState = useCallback(() => {
    if (!mapController) {
      setCanUndo(false);
      setCanRedo(false);
      setHasSelectedMarker(false);
      return;
    }

    let newCanUndo = false;
    let newCanRedo = false;

    if ('canUndo' in mapController && 'canRedo' in mapController) {
      newCanUndo = Boolean(mapController.canUndo);
      newCanRedo = Boolean(mapController.canRedo);
    } else {
      const historyManager = mapController.getCoreHistoryManager?.();
      if (historyManager) {
        newCanUndo = Boolean(historyManager.canUndo);
        newCanRedo = Boolean(historyManager.canRedo);
      }
    }

    let newHasSelected = false;
    const markerManager = mapController.getMarkerManager?.();
    if (markerManager) {
      const selectedMarkers = markerManager.getSelectedMarkers();
      newHasSelected = selectedMarkers.length > 0;
    }

    setCanUndo(newCanUndo);
    setCanRedo(newCanRedo);
    setHasSelectedMarker(newHasSelected);
  }, [mapController]);

  const handleUndo = useCallback(
    (event?: React.MouseEvent) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      if (!mapController) return;

      if ('undo' in mapController && typeof mapController.undo === 'function') {
        mapController.undo();
      } else {
        const historyManager = mapController.getCoreHistoryManager?.();
        if (historyManager) {
          historyManager.undo();
        }
      }
    },
    [mapController]
  );

  const handleRedo = useCallback(
    (event?: React.MouseEvent) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      if (!mapController) return;

      if ('redo' in mapController && typeof mapController.redo === 'function') {
        mapController.redo();
      } else {
        const historyManager = mapController.getCoreHistoryManager?.();
        if (historyManager) {
          historyManager.redo();
        }
      }
    },
    [mapController]
  );

  const handleDelete = useCallback(() => {
    if (!mapController || !hasSelectedMarker) return;

    const deleteEvent = new KeyboardEvent('keydown', {
      key: 'Backspace',
      bubbles: true,
      cancelable: true,
    });

    document.dispatchEvent(deleteEvent);
  }, [mapController, hasSelectedMarker]);

  useEffect(() => {
    if (!mapController) return;

    const historyManager = mapController.getCoreHistoryManager?.();
    if (!historyManager) return;

    const handleHistoryChange = () => updateState();

    historyManager.addEventListener('history:undo', handleHistoryChange);
    historyManager.addEventListener('history:redo', handleHistoryChange);
    historyManager.addEventListener('history:execute', handleHistoryChange);

    updateState();

    return () => {
      historyManager.removeEventListener('history:undo', handleHistoryChange);
      historyManager.removeEventListener('history:redo', handleHistoryChange);
      historyManager.removeEventListener('history:execute', handleHistoryChange);
    };
  }, [mapController, updateState]);

  useEffect(() => {
    if (!mapController) return;

    const markerManager = mapController.getMarkerManager?.();
    if (!markerManager) {
      return;
    }

    const handleSelectionChange = (event: Event) => {
      const { selectedCount } = (event as CustomEvent).detail;
      const hasSelected = selectedCount > 0;
      setHasSelectedMarker(hasSelected);
    };

    markerManager.addEventListener('marker:selectionChanged', handleSelectionChange);

    // Initial state update
    const selectedMarkers = markerManager.getSelectedMarkers();
    setHasSelectedMarker(selectedMarkers.length > 0);

    return () => {
      markerManager.removeEventListener('marker:selectionChanged', handleSelectionChange);
    };
  }, [mapController]);

  return {
    canUndo,
    canRedo,
    hasSelectedMarker,
    handleUndo,
    handleRedo,
    handleDelete,
  };
};
